/**
 * Attachments on screen (web-plan.md F2): albums of 1–10 with ONE height per
 * frame (from wasm — the same geometry the desktop computes, D48's scroll
 * anchoring depends on it) in the shape its first picture asks for (D241), the
 * spoiler overlay, the lightbox, `<video>` on the channel-files route (Range
 * through the Service Worker), the voice-note player over the packed waveform,
 * and the honest notice for a kind this build cannot draw.
 */
import { For, type JSX, Show, createMemo, createSignal } from "solid-js";

import type { Attachment } from "../gen/dto";

import { channelFileUrl } from "../lib/api/rest";
import { escapable } from "../lib/escape";
import type { Dict } from "../lib/i18n";
import { albumHeight, unpackWaveform } from "../lib/wasm";
import { Icon } from "./ds";

const ALBUM_WIDTH = 420;

export function Attachments(props: {
  channelId: number;
  items: Attachment[];
  t: Dict;
}): JSX.Element {
  const visual = createMemo(() =>
    props.items.filter((a) => a.kind === "photo" || a.kind === "video" || a.kind === "video_message"),
  );
  const rest = createMemo(() =>
    props.items.filter((a) => a.kind !== "photo" && a.kind !== "video" && a.kind !== "video_message"),
  );
  return (
    <>
      <Show when={visual().length > 0}>
        <Album channelId={props.channelId} items={visual()} t={props.t} />
      </Show>
      <For each={rest()}>
        {(a) => {
          if (a.kind === "voice") {
            return <VoiceNote channelId={props.channelId} a={a} />;
          }
          if (a.kind === "file") {
            return <FileChip channelId={props.channelId} a={a} />;
          }
          return (
            <div class="unsupported-media">
              <span class="unsupported-media__icon">
                <Icon name="help" size={15} />
              </span>
              <div>
                <span class="unsupported-media__text">{props.t.unsupportedAttachment()}</span>
              </div>
            </div>
          );
        }}
      </For>
    </>
  );
}

function Album(props: { channelId: number; items: Attachment[]; t: Dict }): JSX.Element {
  const [slide, setSlide] = createSignal(0);
  const [lightbox, setLightbox] = createSignal(false);
  // A picture at full size is over everything, including whatever screen the
  // bubble is on, so it is the first thing Escape closes while it is up.
  escapable(lightbox, () => setLightbox(false));
  // One height for every slide, decided before a byte arrives (wasm).
  const height = createMemo(() => albumHeight(props.items, ALBUM_WIDTH));
  // ...and which way the first picture faces, which is what the whole bubble
  // is shaped by (D241).
  const shape = createMemo(() => albumShape(props.items));
  // One measured picture is drawn at its own size instead (D252) — see
  // `albumLone`.
  const lone = createMemo(() => albumLone(props.items));
  const current = () => props.items[Math.min(slide(), props.items.length - 1)]!;

  return (
    <>
      <div
        class={`media-album${props.items.length > 1 ? " media-album--many" : ""}${shape() === "" ? "" : ` ${shape()}`}${
          lone() === null ? "" : " media-album--lone"
        }`}
        style={`--album-w:${ALBUM_WIDTH};--album-h:${height()}${
          lone() === null ? "" : `;--media-w:${lone()![0]};--media-h:${lone()![1]}`
        };border-radius:14px`}
      >
        <Slide
          channelId={props.channelId}
          a={current()}
          onOpen={() => current().kind === "photo" && setLightbox(true)}
        />
        <Show when={props.items.length > 1}>
          <div style="position:absolute;inset-inline:0;bottom:6px;display:flex;justify-content:center;gap:4px">
            <For each={props.items}>
              {(_, i) => (
                <button
                  type="button"
                  style={`width:6px;height:6px;border-radius:50%;border:none;padding:0;cursor:pointer;background:${
                    i() === slide() ? "var(--grays-white)" : "rgba(255,255,255,.45)"
                  }`}
                  onClick={() => setSlide(i())}
                />
              )}
            </For>
          </div>
          <button
            type="button"
            style="position:absolute;left:4px;top:50%;transform:translateY(-50%);background:rgba(0,0,0,.35);border:none;border-radius:50%;width:28px;height:28px;color:#fff;cursor:pointer"
            onClick={() => setSlide((slide() + props.items.length - 1) % props.items.length)}
          >
            <Icon name="chevron_left" size={17} />
          </button>
          <button
            type="button"
            style="position:absolute;right:4px;top:50%;transform:translateY(-50%);background:rgba(0,0,0,.35);border:none;border-radius:50%;width:28px;height:28px;color:#fff;cursor:pointer"
            onClick={() => setSlide((slide() + 1) % props.items.length)}
          >
            <Icon name="chevron_right" size={17} />
          </button>
        </Show>
      </div>
      <Show when={lightbox()}>
        <div
          class="echo-scrim"
          style="display:flex;align-items:center;justify-content:center;z-index:60;position:fixed;inset:0;background:var(--overlays-heavy,rgba(0,0,0,.7))"
          onClick={() => setLightbox(false)}
        >
          <img
            src={channelFileUrl(props.channelId, current().file_id)}
            alt=""
            style="max-width:92vw;max-height:92vh;border-radius:8px"
          />
        </div>
      </Show>
    </>
  );
}

/**
 * Which way the message's first picture faces — the shape `app.css` draws the
 * whole bubble at (D241): 60 % of the chat for a landscape one, wider and
 * twice as tall for a portrait, half again as big for anything else.
 *
 * The desktop asks `echo_wire::album::shape` for this; here it is five lines
 * of TypeScript rather than another wasm export, and they say what
 * `Attachment::aspect` says in Rust — a picture that never told anyone how big
 * it is counts as square. What the shapes are *worth* is in neither client: it
 * is in the stylesheet both of them wear.
 */
function albumShape(items: Attachment[]): string {
  const first = items[0];
  if (!first) return "";
  const w = first.width ?? 0;
  const h = first.height ?? 0;
  if (w <= 0 || h <= 0) return "media-album--square";
  if (w > h) return "media-album--wide";
  return w < h ? "media-album--tall" : "media-album--square";
}

/**
 * The one picture's own pixel size, when the message is one picture that said
 * how big it is (D252) — `null` for an album, which keeps one frame for every
 * slide, and for anything unmeasured.
 *
 * `echo_wire::album::lone` in the desktop, four lines of TypeScript here, for
 * the same reason `albumShape` is: what the two numbers are *worth* is in the
 * stylesheet both clients wear.
 */
function albumLone(items: Attachment[]): [number, number] | null {
  if (items.length !== 1) return null;
  const w = items[0]!.width ?? 0;
  const h = items[0]!.height ?? 0;
  return w > 0 && h > 0 ? [w, h] : null;
}

function Slide(props: { channelId: number; a: Attachment; onOpen: () => void }): JSX.Element {
  const [revealed, setRevealed] = createSignal(!props.a.spoiler);
  const url = () => channelFileUrl(props.channelId, props.a.file_id);
  return (
    <Show
      when={props.a.kind === "photo"}
      fallback={
        // Range through the Service Worker makes seeking work (D47/F2).
        <video
          src={url()}
          controls
          preload="metadata"
          style="width:100%;height:100%;object-fit:contain;background:#000"
        />
      }
    >
      <div style="width:100%;height:100%;position:relative">
        {/* The thumbnail weight behind the preview (D233): fifteen kilobytes
            holding the frame while three hundred are on their way. It is
            *under* the picture, so it needs no state of its own — the preview
            simply covers it when it arrives. */}
        <Show when={props.a.thumb_file_id}>
          {(thumb) => (
            <img
              src={channelFileUrl(props.channelId, thumb())}
              alt=""
              loading="lazy"
              style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:blur(8px);transform:scale(1.06)"
            />
          )}
        </Show>
        <img
          src={url()}
          alt=""
          loading="lazy"
          style={`position:relative;width:100%;height:100%;object-fit:cover;cursor:zoom-in;${revealed() ? "" : "filter:blur(28px);transform:scale(1.06)"}`}
          onClick={() => (revealed() ? props.onOpen() : setRevealed(true))}
        />
        <Show when={!revealed()}>
          <button
            type="button"
            style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:transparent;border:none;color:#fff;cursor:pointer;font-size:13px"
            onClick={() => setRevealed(true)}
          >
            <Icon name="visibility" size={22} />
          </button>
        </Show>
      </div>
    </Show>
  );
}

function VoiceNote(props: { channelId: number; a: Attachment }): JSX.Element {
  let audio!: HTMLAudioElement;
  const [playing, setPlaying] = createSignal(false);
  const [at, setAt] = createSignal(0);
  const buckets = createMemo(() => {
    if (!props.a.waveform) return new Uint8Array(100);
    try {
      return unpackWaveform(props.a.waveform);
    } catch {
      return new Uint8Array(100);
    }
  });
  const durationS = () => (props.a.duration_ms ?? 0) / 1000;

  return (
    <div class="voice-note" style="display:flex;align-items:center;gap:10px;min-width:220px;padding:4px 0">
      <button
        type="button"
        style="width:34px;height:34px;border-radius:50%;border:none;background:var(--accent-fill);color:#fff;cursor:pointer;flex-shrink:0"
        onClick={() => {
          if (playing()) audio.pause();
          else void audio.play();
        }}
      >
        <Icon name={playing() ? "pause" : "play_arrow"} size={18} filled />
      </button>
      <div
        style="display:flex;align-items:flex-end;gap:1px;height:26px;flex:1;cursor:pointer"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const frac = (e.clientX - r.left) / r.width;
          if (durationS() > 0) audio.currentTime = frac * durationS();
        }}
      >
        <For each={[...buckets()]}>
          {(v, i) => (
            <span
              style={`flex:1;min-width:1px;border-radius:1px;height:${Math.max(8, (v / 31) * 100)}%;background:${
                durationS() > 0 && i() / 100 <= at() / durationS()
                  ? "var(--accent-fill)"
                  : "var(--fills-secondary)"
              }`}
            />
          )}
        </For>
      </div>
      <span class="message-meta__time">
        {Math.floor(at() / 60)}:{String(Math.floor(at() % 60)).padStart(2, "0")}
      </span>
      <audio
        ref={audio}
        src={channelFileUrl(props.channelId, props.a.file_id)}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setAt(0);
        }}
        onTimeUpdate={() => setAt(audio.currentTime)}
      />
    </div>
  );
}

function FileChip(props: { channelId: number; a: Attachment }): JSX.Element {
  const size = () => {
    const s = props.a.size;
    if (s > 1024 * 1024) return `${(s / (1024 * 1024)).toFixed(1)} MiB`;
    if (s > 1024) return `${Math.round(s / 1024)} KiB`;
    return `${s} B`;
  };
  return (
    <a
      class="file-attachment"
      href={channelFileUrl(props.channelId, props.a.file_id)}
      download={props.a.file_name ?? "file"}
      style="display:flex;align-items:center;gap:8px;text-decoration:none;color:inherit;padding:4px 0"
    >
      <Icon name="description" size={22} />
      <span style="display:flex;flex-direction:column;min-width:0">
        <span style="font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
          {props.a.file_name ?? props.a.file_id.slice(0, 12)}
        </span>
        <span style="font-size:12px;opacity:.7">{size()}</span>
      </span>
    </a>
  );
}
